import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../api";
import { useAuth } from "../AuthContext";
import StaffLayout from "../StaffLayout";
import { Trash2, Save } from "lucide-react";

const inputCls =
  "w-full bg-cream border border-ink/15 rounded-sm px-3 py-2 focus:outline-none focus:border-matcha-dark transition-colors text-ink text-sm";

const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const mins = ["00", "15", "30", "45"];

function todayISO() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}
function thisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState("attendance");
  const [month, setMonth] = useState(thisMonth());

  // attendance state
  const [date, setDate] = useState(todayISO());
  const [startH, setStartH] = useState("11");
  const [startM, setStartM] = useState("00");
  const [endH, setEndH] = useState("17");
  const [endM, setEndM] = useState("00");
  const [breakMin, setBreakMin] = useState(0); // 0 / 30 / 45 / 60
  const [att, setAtt] = useState([]);
  const [attMsg, setAttMsg] = useState("");
  const [attErr, setAttErr] = useState("");
  const [payroll, setPayroll] = useState(null);

  const isPending = user && !user.active && user.role !== "admin";

  const loadAtt = async () => {
    try {
      const { data } = await api.get(`/attendance/me?month=${month}`);
      setAtt(data);
    } catch {
      // ignore
    }
  };

  const loadPay = async () => {
    try {
      const { data } = await api.get(`/payroll/me?month=${month}`);
      setPayroll(data);
    } catch {
      setPayroll(null);
    }
  };

  useEffect(() => {
    loadAtt();
    loadPay();
    // eslint-disable-next-line
  }, [month]);

  const submitAtt = async (e) => {
    e.preventDefault();
    setAttErr("");
    setAttMsg("");
    try {
      const body = {
        date,
        start_time: `${startH}:${startM}`,
        end_time: `${endH}:${endM}`,
        break_minutes: Number(breakMin) || 0,
      };
      await api.post("/attendance", body);
      setAttMsg("Attendance saved.");
      await loadAtt();
      await loadPay();
    } catch (e) {
      setAttErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const deleteAtt = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/attendance/${id}`);
    await loadAtt();
    await loadPay();
  };

  const totalHours = useMemo(
    () => att.reduce((s, a) => s + (a.hours || 0), 0).toFixed(2),
    [att],
  );

  return (
    <StaffLayout>
      <div className="mb-10">
        <p className="overline text-matcha-dark">— Welcome back</p>
        <h1
          className="font-display text-ink mt-2"
          style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)", letterSpacing: "0.04em" }}
        >
          {user?.preferred_name || user?.full_name || "Staff"}
        </h1>
        <p className="text-ink-soft mt-2 text-sm">
          {user?.email}
          {isPending && (
            <span
              data-testid="pending-banner"
              className="ml-3 inline-block bg-yellow-100 text-yellow-900 px-3 py-1 rounded-full overline"
            >
              Pending Admin Approval
            </span>
          )}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-6 border-b border-ink/15 mb-8">
        {[
          { k: "attendance", label: "Attendance" },
          { k: "payroll", label: "Payroll" },
          { k: "profile", label: "Profile" },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`pb-3 -mb-px overline transition-colors ${
              tab === t.k
                ? "text-matcha-dark border-b-2 border-matcha-dark"
                : "text-ink-soft hover:text-ink"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "attendance" && (
        <div className="grid md:grid-cols-12 gap-8">
          <section className="md:col-span-5 bg-bone border border-ink/10 rounded-sm p-6 md:p-8 h-fit">
            <h3 className="font-display text-xl text-ink mb-4" style={{ letterSpacing: "0.05em" }}>
              Log a shift
            </h3>
            <form onSubmit={submitAtt} className="space-y-4" data-testid="attendance-form">
              <div>
                <label className="overline text-ink-soft block mb-2">Date</label>
                <input
                  data-testid="att-date"
                  type="date"
                  required
                  className={inputCls}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline text-ink-soft block mb-2">Start</label>
                  <div className="flex gap-2">
                    <select data-testid="att-start-h" className={inputCls} value={startH} onChange={(e) => setStartH(e.target.value)}>
                      {hours.map((h) => <option key={h}>{h}</option>)}
                    </select>
                    <select data-testid="att-start-m" className={inputCls} value={startM} onChange={(e) => setStartM(e.target.value)}>
                      {mins.map((m) => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="overline text-ink-soft block mb-2">End</label>
                  <div className="flex gap-2">
                    <select data-testid="att-end-h" className={inputCls} value={endH} onChange={(e) => setEndH(e.target.value)}>
                      {hours.map((h) => <option key={h}>{h}</option>)}
                    </select>
                    <select data-testid="att-end-m" className={inputCls} value={endM} onChange={(e) => setEndM(e.target.value)}>
                      {mins.map((m) => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <label className="flex items-center gap-3 pt-1 select-none cursor-pointer">
                <span className="overline text-ink-soft">Break</span>
              </label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { v: 0, label: "None" },
                  { v: 30, label: "30 min" },
                  { v: 45, label: "45 min" },
                  { v: 60, label: "1 hour" },
                ].map((b) => (
                  <button
                    key={b.v}
                    type="button"
                    data-testid={`break-${b.v}`}
                    onClick={() => setBreakMin(b.v)}
                    className={`py-2.5 rounded-sm border text-xs uppercase tracking-[0.18em] transition-colors ${
                      breakMin === b.v
                        ? "bg-ink text-bone border-ink"
                        : "border-ink/20 text-ink-soft hover:border-ink/40"
                    }`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
              {attErr && <p data-testid="att-error" className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm border border-red-200">{attErr}</p>}
              {attMsg && <p data-testid="att-success" className="text-sm text-matcha-dark">{attMsg}</p>}
              <button
                data-testid="att-submit"
                disabled={isPending}
                className="w-full bg-ink text-bone rounded-full py-2.5 text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors disabled:opacity-50"
              >
                {isPending ? "Awaiting approval" : "Save Attendance"}
              </button>
            </form>
          </section>

          <section className="md:col-span-7 min-w-0">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <h3 className="font-display text-xl text-ink" style={{ letterSpacing: "0.05em" }}>
                History
              </h3>
              <input
                data-testid="month-filter"
                type="month"
                className={inputCls + " w-auto max-w-[160px]"}
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              />
              <span className="overline text-ink-soft whitespace-nowrap ml-auto">
                Total: <span className="text-matcha-dark">{totalHours}h</span>
              </span>
            </div>
            <div className="bg-bone border border-ink/10 rounded-sm overflow-x-auto">
              {att.length === 0 ? (
                <p className="p-8 text-center text-ink-soft text-sm">No entries yet for {month}.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-sage/40">
                    <tr className="text-left text-ink-soft">
                      <th className="px-3 sm:px-4 py-3 overline">Date</th>
                      <th className="px-3 sm:px-4 py-3 overline">Start</th>
                      <th className="px-3 sm:px-4 py-3 overline">End</th>
                      <th className="px-3 sm:px-4 py-3 overline">Break</th>
                      <th className="px-3 sm:px-4 py-3 overline">Hours</th>
                      <th className="px-3 sm:px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {att.map((a) => (
                      <tr key={a.id} className="border-t border-ink/5" data-testid={`att-row-${a.date}`}>
                        <td className="px-3 sm:px-4 py-3 whitespace-nowrap">{a.date}</td>
                        <td className="px-3 sm:px-4 py-3 whitespace-nowrap">{a.start_time}</td>
                        <td className="px-3 sm:px-4 py-3 whitespace-nowrap">{a.end_time}</td>
                        <td className="px-3 sm:px-4 py-3 text-ink-soft whitespace-nowrap">
                          {a.break_start && a.break_end
                            ? `${a.break_start}–${a.break_end} (${a.break_minutes}m)`
                            : "—"}
                        </td>
                        <td className="px-3 sm:px-4 py-3 text-matcha-dark whitespace-nowrap">{a.hours}</td>
                        <td className="px-3 sm:px-4 py-3 text-right">
                          <button
                            data-testid={`att-del-${a.date}`}
                            onClick={() => deleteAtt(a.id)}
                            className="text-ink-soft hover:text-red-600 transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </div>
      )}

      {tab === "payroll" && (
        <section className="max-w-2xl">
          <div className="flex items-center gap-3 mb-6">
            <label className="overline text-ink-soft">Month</label>
            <input
              data-testid="payroll-month"
              type="month"
              className={inputCls + " w-auto"}
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
          </div>
          {!payroll ? (
            <p className="text-ink-soft">No payroll data.</p>
          ) : (
            <div className="bg-bone border border-ink/10 rounded-sm p-8" data-testid="payroll-card">
              <p className="overline text-ink-soft">{month} · {payroll.employment_type || "Not assigned"}</p>
              <p className="font-display text-5xl text-ink mt-3" style={{ letterSpacing: "0.02em" }}>
                RM {Number(payroll.estimated_pay || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
              <div className="grid grid-cols-3 mt-6 gap-6 text-sm">
                <div>
                  <p className="overline text-ink-soft">Days</p>
                  <p className="text-ink mt-1">{payroll.days_worked}</p>
                </div>
                <div>
                  <p className="overline text-ink-soft">Hours</p>
                  <p className="text-ink mt-1">{payroll.total_hours}</p>
                </div>
                <div>
                  <p className="overline text-ink-soft">{payroll.employment_type === "part_time" ? "Hourly" : "Basic"}</p>
                  <p className="text-ink mt-1">
                    RM {(payroll.hourly_rate ?? payroll.basic_salary ?? 0)}
                  </p>
                </div>
              </div>
              {!payroll.employment_type && (
                <p className="mt-6 text-xs text-yellow-800 bg-yellow-50 px-3 py-2 rounded-sm">
                  Admin has not set your employment type yet.
                </p>
              )}
            </div>
          )}
        </section>
      )}

      {tab === "profile" && <ProfileEditor onSaved={refresh} />}
    </StaffLayout>
  );
}

function ProfileEditor({ onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState(() => ({
    full_name: user?.full_name || "",
    preferred_name: user?.preferred_name || "",
    date_of_birth: user?.date_of_birth || "",
    contact_no: user?.contact_no || "",
    nric_no: user?.nric_no || "",
    gender: user?.gender || "Male",
    address: user?.address || "",
    socso: user?.socso || "",
    epf: user?.epf || "",
    ec_name: user?.emergency_contact?.name || "",
    ec_phone: user?.emergency_contact?.phone || "",
    ec_relationship: user?.emergency_contact?.relationship || "",
  }));
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");
    try {
      await api.patch("/auth/me", {
        full_name: form.full_name,
        preferred_name: form.preferred_name,
        date_of_birth: form.date_of_birth,
        contact_no: form.contact_no,
        nric_no: form.nric_no,
        gender: form.gender,
        address: form.address,
        socso: form.socso,
        epf: form.epf,
        emergency_contact: {
          name: form.ec_name,
          phone: form.ec_phone,
          relationship: form.ec_relationship,
        },
      });
      setMsg("Saved.");
      onSaved && onSaved();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <form onSubmit={save} className="bg-bone border border-ink/10 rounded-sm p-8 space-y-8 max-w-4xl" data-testid="profile-form">
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Full name"><input className={inputCls} value={form.full_name} onChange={upd("full_name")} required /></Field>
        <Field label="Preferred name"><input className={inputCls} value={form.preferred_name} onChange={upd("preferred_name")} /></Field>
        <Field label="Date of birth"><input type="date" className={inputCls} value={form.date_of_birth} onChange={upd("date_of_birth")} /></Field>
        <Field label="Contact number"><input className={inputCls} value={form.contact_no} onChange={upd("contact_no")} /></Field>
        <Field label="NRIC"><input className={inputCls} value={form.nric_no} onChange={upd("nric_no")} /></Field>
        <Field label="Gender">
          <select className={inputCls} value={form.gender} onChange={upd("gender")}>
            <option>Male</option><option>Female</option><option>Other</option>
          </select>
        </Field>
        <Field label="Address" span={2}>
          <textarea rows={2} className={inputCls} value={form.address} onChange={upd("address")} />
        </Field>
        <Field label="SOCSO"><input className={inputCls} value={form.socso} onChange={upd("socso")} /></Field>
        <Field label="EPF"><input className={inputCls} value={form.epf} onChange={upd("epf")} /></Field>
      </div>
      <div>
        <h3 className="overline text-matcha-dark mb-4">— Emergency Contact</h3>
        <div className="grid md:grid-cols-3 gap-5">
          <Field label="Name"><input className={inputCls} value={form.ec_name} onChange={upd("ec_name")} /></Field>
          <Field label="Phone"><input className={inputCls} value={form.ec_phone} onChange={upd("ec_phone")} /></Field>
          <Field label="Relationship"><input className={inputCls} value={form.ec_relationship} onChange={upd("ec_relationship")} /></Field>
        </div>
      </div>
      {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm">{err}</p>}
      {msg && <p className="text-sm text-matcha-dark">{msg}</p>}
      <button
        data-testid="profile-save"
        className="inline-flex items-center gap-2 rounded-full px-7 py-2.5 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors"
      >
        <Save size={14} /> Save Profile
      </button>
    </form>
  );
}

function Field({ label, children, span = 1 }) {
  return (
    <div className={span === 2 ? "md:col-span-2" : ""}>
      <label className="overline text-ink-soft block mb-2">{label}</label>
      {children}
    </div>
  );
}
